import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import NavBar from './NavBar';
import { getUserLeads, getUserProfile } from '../services/userService';
import { updateLeadInFirestore, deleteLeadFromFirestore } from '../services/firebaseService';
import { base64ToDataUrl } from '../utils/imageToBase64';
import { Timestamp } from 'firebase/firestore';
import Icon from './Icon';

function LeadsList() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [editedDrafts, setEditedDrafts] = useState({}); // Store edited drafts: { leadId: { formal: '...', casual: '...' } }
  
  // Filter and sort state
  const [sortBy, setSortBy] = useState('dateAdded'); // 'dateAdded', 'title', 'company', 'industry', 'convention'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [filterBy, setFilterBy] = useState('all'); // 'all', 'title', 'industry', 'convention'
  const [filterValue, setFilterValue] = useState(''); // The value to filter by
  
  // Reminder state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedLeadForReminder, setSelectedLeadForReminder] = useState(null);
  const [reminderDays, setReminderDays] = useState(7);
  const [reminderNote, setReminderNote] = useState('');

  useEffect(() => {
    loadLeads();
    loadUserProfile();
  }, [user]);

  // Re-render when profile loads to update personalized drafts
  useEffect(() => {
    // This ensures drafts update when profile is loaded
  }, [userProfile]);

  const loadUserProfile = async () => {
    if (!user?.uid) return;
    try {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    } catch (error) {
      console.warn('Could not load user profile:', error);
    }
  };

  // Helper function to remove duplicate closings
  const removeDuplicateClosings = (text, userName) => {
    if (!text) return text;
    
    // More comprehensive pattern to match closing signatures with optional name
    // Matches: "Best regards,\nFrank Zhao" or "Best regards,\n\nFrank Zhao" or just "Best regards,"
    const closingPattern = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*/gi;
    
    // Find all closing matches
    const matches = [];
    let match;
    while ((match = closingPattern.exec(text)) !== null) {
      matches.push({
        text: match[0],
        index: match.index,
        fullMatch: match[0]
      });
    }
    
    // If we have multiple closings, keep only the last one
    if (matches.length > 1) {
      // Sort by index to process in order
      matches.sort((a, b) => a.index - b.index);
      
      // Keep the last closing, remove all others
      const lastMatch = matches[matches.length - 1];
      let cleaned = text;
      
      // Remove all closings except the last one (working backwards)
      for (let i = matches.length - 2; i >= 0; i--) {
        const matchToRemove = matches[i];
        const beforeMatch = cleaned.substring(0, matchToRemove.index).trimEnd();
        const afterMatch = cleaned.substring(matchToRemove.index + matchToRemove.text.length).trimStart();
        
        // If there's content after, check if we need to preserve spacing
        if (afterMatch && !afterMatch.startsWith('\n')) {
          cleaned = beforeMatch + '\n\n' + afterMatch;
        } else {
          cleaned = beforeMatch + afterMatch;
        }
      }
      
      return cleaned;
    }
    
    return text;
  };

  const getPersonalizedDraft = (draftText, leadConvention = null, leadName = null) => {
    if (!draftText) return '';
    
    // Get user's name from profile, fallback to email
    const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
    
    // Create personalized introduction
    let intro = '';
    if (userProfile) {
      const parts = [];
      if (userProfile.name) parts.push(`I'm ${userProfile.name}`);
      if (userProfile.position && userProfile.company) {
        parts.push(`the ${userProfile.position} at ${userProfile.company}`);
      } else if (userProfile.position) {
        parts.push(`a ${userProfile.position}`);
      } else if (userProfile.company) {
        parts.push(`from ${userProfile.company}`);
      }
      if (parts.length > 0) {
        intro = parts.join(', ') + '. ';
      }
    }

    // Replace placeholders with actual name (handle multiple formats)
    let personalized = draftText;
    
    // First, clean up any duplicate closings/signatures BEFORE other processing
    personalized = removeDuplicateClosings(personalized, userName);
    
    // Replace [Your Name] first (with brackets)
    personalized = personalized.replace(/\[Your Name\]/gi, userName);
    // Then replace Your Name (without brackets) - but be careful not to replace it if it's part of a word
    // Only replace "Your Name" if it's at the end of a line (likely in closing signature)
    personalized = personalized.replace(/\bYour Name\b/gi, userName);
    // DO NOT replace event names - they should stay as the convention name
    // The GPT should already have the correct convention name in the draft
    
    // Ensure greeting exists - if not, add one
    const hasGreeting = /^(Dear|Hi|Hello|Hey)\s+[^,\n]+[,]?\s*\n?/i.test(personalized.trim());
    if (!hasGreeting) {
      // Try to get lead name from context - for now, use generic greeting
      personalized = `Dear [Contact Name],\n\n${personalized.trim()}`;
    }
    
    // Ensure closing exists - check more thoroughly
    const closingPattern = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n*\s*[^\n]*$/i;
    const hasClosing = closingPattern.test(personalized.trim());
    
    if (!hasClosing) {
      // Add closing if missing
      personalized = personalized.trim() + `\n\nBest regards,\n${userName}`;
    } else {
      // Replace "Your Name" in closing if it exists, and ensure only one closing
      personalized = personalized.replace(/(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n*\s*Your\s+Name$/i, `$1,\n${userName}`);
      // Clean up any remaining duplicates after replacement
      personalized = removeDuplicateClosings(personalized, userName);
    }
    
    // Add personalized introduction after greeting (Dear/Hi/Hello [Name],) if we have profile info
    if (intro && !personalized.includes(intro)) {
      // Look for common greeting patterns: "Dear [Name],", "Hi [Name],", "Hello [Name],"
      // Case insensitive, with optional comma, handles both with and without newline
      const greetingPatterns = [
        /(Dear\s+[^,\n]+[,]?\s*\n?)/i,
        /(Hi\s+[^,\n]+[,]?\s*\n?)/i,
        /(Hello\s+[^,\n]+[,]?\s*\n?)/i,
        /(Hey\s+[^,\n]+[,]?\s*\n?)/i
      ];
      
      let match = null;
      for (const pattern of greetingPatterns) {
        match = personalized.match(pattern);
        if (match) break;
      }
      
      if (match) {
        // Insert intro right after the greeting
        const insertPosition = match.index + match[0].length;
        // Add newline if not already there, then intro
        const needsNewline = !match[0].endsWith('\n');
        personalized = personalized.slice(0, insertPosition) + 
                      (needsNewline ? '\n' : '') + intro + 
                      personalized.slice(insertPosition);
      } else {
        // If no greeting found, add at the beginning
        personalized = intro + personalized;
      }
    }
    
    // Final validation: ensure draft structure is correct
    personalized = validateDraftStructure(personalized, userName, leadName);
    
    return personalized;
  };

  // Validation function to ensure draft structure is correct
  const validateDraftStructure = (draft, userName, contactName) => {
    if (!draft) return draft;
    
    let validated = draft.trim();
    
    // 1. Remove any duplicate closings (final cleanup) - do this first
    validated = removeDuplicateClosings(validated, userName);
    
    // 2. More aggressive duplicate removal - look for repeated closing patterns
    // Pattern: "Best regards,\nName\n\nBest regards,\nName" or similar
    const duplicateClosingPattern = /((Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]+\s*\n+\s*)(\2[,\s]*\n+\s*[^\n]+)/gi;
    let prevValidated = '';
    while (validated !== prevValidated) {
      prevValidated = validated;
      validated = validated.replace(duplicateClosingPattern, '$1');
    }
    
    // 3. Ensure only one closing exists at the end - more thorough check
    const closingRegex = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*/gi;
    const allClosings = [];
    let match;
    while ((match = closingRegex.exec(validated)) !== null) {
      allClosings.push({
        text: match[0],
        index: match.index
      });
    }
    
    if (allClosings.length > 1) {
      // Keep only the last closing
      const lastClosing = allClosings[allClosings.length - 1];
      const beforeLast = validated.substring(0, lastClosing.index).trimEnd();
      // Remove any other closing patterns from beforeLast
      const cleanedBefore = beforeLast.replace(/(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*/gi, '').trimEnd();
      validated = cleanedBefore + '\n\n' + lastClosing.text.trim();
    }
    
    // 4. Remove duplicate user names in closing (e.g., "Frank Zhao\n\nFrank Zhao")
    const duplicateNamePattern = new RegExp(`(${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\s*\\n+\\s*\\1`, 'gi');
    validated = validated.replace(duplicateNamePattern, userName);
    
    // 5. Ensure closing has the user's name (not placeholder)
    const hasClosing = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)/i.test(validated);
    if (hasClosing) {
      // Check if closing ends with user name, if not add it
      const closingWithName = new RegExp(`(Best\\s+regards|Best|Sincerely|Regards|Thanks|Thank\\s+you)[,\\s]*\\n+\\s*${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      if (!closingWithName.test(validated)) {
        // Replace closing without name or with placeholder
        validated = validated.replace(/(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*([^\n]*)$/i, `$1,\n${userName}`);
      }
    }
    
    return validated.trim();
  };

  const loadLeads = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      setError(null);
      const userLeads = await getUserLeads(user.uid);
      setLeads(userLeads);
      setFilteredLeads(userLeads); // Initialize filtered leads
      
      // Set default selected draft for each lead (prefer 'formal', then first available)
      const defaultDrafts = {};
      userLeads.forEach(lead => {
        if (lead.drafts) {
          const draftTypes = Object.keys(lead.drafts);
          if (draftTypes.length > 0) {
            defaultDrafts[lead.id] = draftTypes.includes('formal') ? 'formal' : draftTypes[0];
          }
        }
      });
      setSelectedDraft(prev => ({ ...prev, ...defaultDrafts }));
    } catch (err) {
      setError('Failed to load leads: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...leads];

    // Apply filter
    if (filterBy !== 'all' && filterValue.trim()) {
      const searchTerm = filterValue.toLowerCase().trim();
      filtered = filtered.filter(lead => {
        switch (filterBy) {
          case 'title':
            return lead.title?.toLowerCase().includes(searchTerm);
          case 'industry':
            return lead.industry_guess?.toLowerCase().includes(searchTerm);
          case 'convention':
            return lead.convention?.toLowerCase().includes(searchTerm);
          case 'company':
            return lead.company?.toLowerCase().includes(searchTerm);
          case 'name':
            return lead.name?.toLowerCase().includes(searchTerm);
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'dateAdded':
          aValue = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          bValue = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          break;
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'company':
          aValue = (a.company || '').toLowerCase();
          bValue = (b.company || '').toLowerCase();
          break;
        case 'industry':
          aValue = (a.industry_guess || '').toLowerCase();
          bValue = (b.industry_guess || '').toLowerCase();
          break;
        case 'convention':
          aValue = (a.convention || '').toLowerCase();
          bValue = (b.convention || '').toLowerCase();
          break;
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortBy === 'dateAdded') {
        // For dates, desc = newest first, asc = oldest first
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      } else {
        // For strings, asc = A-Z, desc = Z-A
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });

    setFilteredLeads(filtered);
  }, [leads, sortBy, sortOrder, filterBy, filterValue]);

  const handleSendEmail = (lead) => {
    const email = lead.email;
    if (!email) {
      alert('Email address is required to send email');
      return;
    }

    // Get user's name from profile, fallback to email
    const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
    
    // Create personalized introduction
    let intro = '';
    if (userProfile) {
      const parts = [];
      if (userProfile.name) parts.push(`I'm ${userProfile.name}`);
      if (userProfile.position && userProfile.company) {
        parts.push(`the ${userProfile.position} at ${userProfile.company}`);
      } else if (userProfile.position) {
        parts.push(`a ${userProfile.position}`);
      } else if (userProfile.company) {
        parts.push(`from ${userProfile.company}`);
      }
      if (parts.length > 0) {
        intro = parts.join(', ') + '. ';
      }
    }

    const draftType = selectedDraft[lead.id] || 'formal';
    let draft = lead.drafts?.[draftType] || '';
    
    // Replace placeholder with actual name
    // DO NOT replace event/convention names - they should remain as the actual event name
    draft = draft.replace(/\[Your Name\]/gi, userName);
    draft = draft.replace(/\bYour Name\b/gi, userName);
    
    // Add personalized introduction at the beginning if we have profile info
    if (intro && !draft.includes(intro)) {
      draft = intro + draft;
    }
    
    const conventionName = lead.convention || 'the event';
    const subject = encodeURIComponent(`Great meeting you at ${conventionName} - ${userName}`);
    const body = encodeURIComponent(draft);
    
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleOpenLinkedIn = (lead) => {
    // Priority 1: Direct LinkedIn profile URL if available
    if (lead.linkedin_url) {
      window.open(lead.linkedin_url, '_blank');
    } 
    // Priority 2: Google search query as fallback
    else if (lead.linkedin_search_query) {
      window.open(lead.linkedin_search_query, '_blank');
    } 
    // Priority 3: Generate Google search from name and company
    else {
      const query = encodeURIComponent(`${lead.name || ''} ${lead.company || ''} LinkedIn`.trim());
      window.open(`https://www.google.com/search?q=${query}`, '_blank');
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead.id);
    setEditedData({ ...lead });
    // Initialize edited drafts with current drafts
    if (lead.drafts) {
      setEditedDrafts(prev => ({
        ...prev,
        [lead.id]: { ...lead.drafts }
      }));
    }
  };

  const handleCancelEdit = () => {
    setEditingLead(null);
    setEditedData(null);
    setEditedDrafts(prev => {
      const newState = { ...prev };
      delete newState[editingLead];
      return newState;
    });
  };

  const handleDraftChange = (leadId, draftType, value) => {
    setEditedDrafts(prev => ({
      ...prev,
      [leadId]: {
        ...prev[leadId],
        [draftType]: value
      }
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingLead || !user?.uid || !editedData) return;

    try {
      // Validate and clean all edited drafts before saving
      const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
      const lead = leads.find(l => l.id === editingLead);
      const cleanedDrafts = {};
      
      if (editedDrafts[editingLead]) {
        Object.keys(editedDrafts[editingLead]).forEach(draftType => {
          let cleaned = editedDrafts[editingLead][draftType];
          cleaned = removeDuplicateClosings(cleaned, userName);
          cleaned = validateDraftStructure(cleaned, userName, lead?.name);
          cleanedDrafts[draftType] = cleaned;
        });
      }
      
      // Merge edited drafts into editedData
      const updatedData = {
        ...editedData,
        drafts: {
          ...editedData.drafts,
          ...cleanedDrafts
        }
      };
      
      // Remove id and timestamp fields before saving
      const { id, createdAt, updatedAt, ...dataToSave } = updatedData;
      await updateLeadInFirestore(editingLead, user.uid, dataToSave);
      
      // Update local state
      setLeads(leads.map(lead => 
        lead.id === editingLead ? { ...lead, ...updatedData } : lead
      ));
      
      setEditingLead(null);
      setEditedData(null);
      setEditedDrafts(prev => {
        const newState = { ...prev };
        delete newState[editingLead];
        return newState;
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Failed to update lead: ' + error.message);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!leadId || !user?.uid) return;
    
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteLeadFromFirestore(leadId, user.uid);
      // Remove from local state
      setLeads(leads.filter(lead => lead.id !== leadId));
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSetReminder = (lead) => {
    setSelectedLeadForReminder(lead);
    // Pre-fill with existing reminder if any
    if (lead.reminderDate) {
      const reminderDate = lead.reminderDate?.toDate ? lead.reminderDate.toDate() : new Date(lead.reminderDate);
      const now = new Date();
      const daysDiff = Math.ceil((reminderDate - now) / (1000 * 60 * 60 * 24));
      if (daysDiff > 0) {
        setReminderDays(daysDiff);
      }
    }
    if (lead.reminderNote) {
      setReminderNote(lead.reminderNote);
    } else {
      setReminderNote('');
    }
    setShowReminderModal(true);
  };

  const handleSaveReminder = async () => {
    if (!selectedLeadForReminder || !user?.uid) return;

    try {
      const now = new Date();
      const reminderDate = new Date(now);
      reminderDate.setDate(reminderDate.getDate() + reminderDays);

      const reminderData = {
        reminderDate: Timestamp.fromDate(reminderDate),
        reminderDays: reminderDays,
        reminderNote: reminderNote.trim() || null,
        reminderSetAt: Timestamp.now(),
      };

      await updateLeadInFirestore(selectedLeadForReminder.id, user.uid, reminderData);

      // Update local state
      setLeads(leads.map(lead =>
        lead.id === selectedLeadForReminder.id
          ? { ...lead, ...reminderData }
          : lead
      ));

      setShowReminderModal(false);
      setSelectedLeadForReminder(null);
      setReminderDays(7);
      setReminderNote('');
    } catch (error) {
      console.error('Error setting reminder:', error);
      alert('Failed to set reminder: ' + error.message);
    }
  };

  const handleDeleteReminder = async (leadId) => {
    if (!leadId || !user?.uid) return;

    if (!window.confirm('Are you sure you want to delete this reminder?')) {
      return;
    }

    try {
      const reminderData = {
        reminderDate: null,
        reminderDays: null,
        reminderNote: null,
        reminderSetAt: null,
      };

      await updateLeadInFirestore(leadId, user.uid, reminderData);

      // Update local state
      setLeads(leads.map(lead =>
        lead.id === leadId
          ? { ...lead, ...reminderData }
          : lead
      ));
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Failed to delete reminder: ' + error.message);
    }
  };

  const formatReminderDate = (reminderDate) => {
    if (!reminderDate) return null;
    const date = reminderDate?.toDate ? reminderDate.toDate() : new Date(reminderDate);
    const now = new Date();
    const daysDiff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      return { text: 'Overdue', isOverdue: true, days: Math.abs(daysDiff) };
    } else if (daysDiff === 0) {
      return { text: 'Today', isToday: true, days: 0 };
    } else if (daysDiff === 1) {
      return { text: 'Tomorrow', days: 1 };
    } else if (daysDiff <= 7) {
      return { text: `In ${daysDiff} days`, days: daysDiff };
    } else {
      return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), days: daysDiff };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-600 dark:text-zinc-400">Loading leads...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-red-400 mb-4">{error}</div>
          <button
            onClick={loadLeads}
            className="px-4 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    const values = leads
      .map(lead => lead[field])
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return values;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      <NavBar title={`My Leads (${filteredLeads.length}${filteredLeads.length !== leads.length ? ` / ${leads.length}` : ''})`} />

      <main className="max-w-md mx-auto px-4 py-6">
        {leads.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📇</div>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg mb-2">No leads yet</p>
            <p className="text-zinc-500 dark:text-zinc-500 text-sm">Start scanning business cards to see them here</p>
          </div>
        ) : (
          <>
            {/* Filter and Sort Controls */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 mb-6 border border-zinc-200 dark:border-zinc-800 space-y-4">
              {/* Sort Controls */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">Sort By</label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-600"
                  >
                    <option value="dateAdded">Date Added</option>
                    <option value="name">Name</option>
                    <option value="title">Title</option>
                    <option value="company">Company</option>
                    <option value="industry">Industry</option>
                    <option value="convention">Convention</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-4 py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm transition-colors"
                    title={sortOrder === 'asc' ? 'Ascending (A-Z, Oldest First)' : 'Descending (Z-A, Newest First)'}
                  >
                    {sortBy === 'dateAdded' 
                      ? (sortOrder === 'desc' ? <><Icon emoji="⬇️" size="text-sm" className="mr-1" /> Newest</> : <><Icon emoji="⬆️" size="text-sm" className="mr-1" /> Oldest</>)
                      : (sortOrder === 'asc' ? <><Icon emoji="⬆️" size="text-sm" className="mr-1" /> A-Z</> : <><Icon emoji="⬇️" size="text-sm" className="mr-1" /> Z-A</>)
                    }
                  </button>
                </div>
              </div>

              {/* Filter Controls */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">Filter By</label>
                <div className="flex gap-2">
                  <select
                    value={filterBy}
                    onChange={(e) => {
                      setFilterBy(e.target.value);
                      setFilterValue(''); // Clear filter value when changing filter type
                    }}
                    className="flex-1 px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-600"
                  >
                    <option value="all">All Leads</option>
                    <option value="name">Name</option>
                    <option value="title">Title</option>
                    <option value="company">Company</option>
                    <option value="industry">Industry</option>
                    <option value="convention">Convention</option>
                  </select>
                </div>
                {filterBy !== 'all' && (
                  <div className="relative">
                    <input
                      type="text"
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      placeholder={`Search by ${filterBy}...`}
                      className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600"
                    />
                    {filterValue && (
                      <button
                        onClick={() => setFilterValue('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
                {filterBy !== 'all' && getUniqueValues(
                  filterBy === 'title' ? 'title' :
                  filterBy === 'industry' ? 'industry_guess' :
                  filterBy === 'convention' ? 'convention' :
                  filterBy === 'company' ? 'company' :
                  'name'
                ).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {getUniqueValues(
                      filterBy === 'title' ? 'title' :
                      filterBy === 'industry' ? 'industry_guess' :
                      filterBy === 'convention' ? 'convention' :
                      filterBy === 'company' ? 'company' :
                      'name'
                    ).slice(0, 10).map((value) => (
                      <button
                        key={value}
                        onClick={() => setFilterValue(value)}
                        className={`px-2 py-1 rounded-lg text-xs transition-colors ${
                          filterValue === value
                            ? 'bg-blue-600 text-white'
                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Leads List */}
            <div className="space-y-4">
              {filteredLeads.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
                  <Icon emoji="🔍" size="text-4xl" className="mb-4" />
                  <p className="text-zinc-600 dark:text-zinc-400 text-lg mb-2">No leads match your filters</p>
                  <button
                    onClick={() => {
                      setFilterBy('all');
                      setFilterValue('');
                    }}
                    className="text-sm text-blue-400 hover:text-blue-300"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredLeads.map((lead) => (
              <div
                key={lead.id}
                className="bg-white dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors"
              >
                {/* Thumbnails */}
                {lead.thumbnailBase64 && (
                  <div className="mb-4 space-y-2">
                    <div className="bg-zinc-100 dark:bg-zinc-800 rounded-lg overflow-hidden">
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 p-1.5 bg-zinc-200 dark:bg-zinc-700 px-2">Front Side</p>
                      <img
                        src={base64ToDataUrl(lead.thumbnailBase64)}
                        alt="Business card front"
                        className="w-full h-auto max-h-48 object-contain"
                      />
                    </div>
                    {lead.thumbnailBackBase64 && (
                      <div className="bg-zinc-100 dark:bg-zinc-800 rounded-lg overflow-hidden">
                        <p className="text-xs text-zinc-600 dark:text-zinc-400 p-1.5 bg-zinc-200 dark:bg-zinc-700 px-2">Back Side</p>
                        <img
                          src={base64ToDataUrl(lead.thumbnailBackBase64)}
                          alt="Business card back"
                          className="w-full h-auto max-h-48 object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Lead Info - Edit Mode or View Mode */}
                {editingLead === lead.id ? (
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Name</label>
                      <input
                        type="text"
                        value={editedData?.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Title</label>
                      <input
                        type="text"
                        value={editedData?.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Company</label>
                      <input
                        type="text"
                        value={editedData?.company || ''}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Email</label>
                      <input
                        type="email"
                        value={editedData?.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Phone (all numbers)</label>
                      <input
                        type="text"
                        value={editedData?.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                        placeholder="Office: +1-555-1234, Cell: +1-555-5678"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Address</label>
                      <textarea
                        value={editedData?.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                        rows="2"
                        placeholder="Street, City, State, Zip, Country"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Website</label>
                      <input
                        type="text"
                        value={editedData?.website || ''}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">LinkedIn URL</label>
                      <input
                        type="text"
                        value={editedData?.linkedin_url || ''}
                        onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Convention/Event</label>
                      <input
                        type="text"
                        value={editedData?.convention || ''}
                        onChange={(e) => handleInputChange('convention', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Industry</label>
                      <input
                        type="text"
                        value={editedData?.industry_guess || ''}
                        onChange={(e) => handleInputChange('industry_guess', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-600 dark:text-zinc-400 mb-1">Icebreaker</label>
                      <textarea
                        value={editedData?.icebreaker || ''}
                        onChange={(e) => handleInputChange('icebreaker', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm"
                        rows="2"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 mb-4">
                    {lead.name && (
                      <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{lead.name}</h3>
                    )}
                    {lead.title && (
                      <p className="text-zinc-600 dark:text-zinc-400 text-sm">{lead.title}</p>
                    )}
                    {lead.company && (
                      <p className="text-zinc-300 font-medium">{lead.company}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
                      {lead.email && <span><Icon emoji="📧" size="text-xs" className="mr-1" />{lead.email}</span>}
                      {lead.phone && <span><Icon emoji="📞" size="text-xs" className="mr-1" />{lead.phone}</span>}
                      {lead.website && <span><Icon emoji="🌐" size="text-xs" className="mr-1" />{lead.website}</span>}
                    </div>
                    {lead.industry_guess && (
                      <p className="text-xs text-zinc-500">Industry: {lead.industry_guess}</p>
                    )}
                  </div>
                )}

                {/* Icebreaker */}
                {lead.icebreaker && (
                  <div className="mb-4 p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-1 flex items-center gap-1">
                      <Icon emoji="💡" size="text-xs" /> Icebreaker
                    </p>
                    <p className="text-sm text-zinc-700 dark:text-zinc-300">{lead.icebreaker}</p>
                  </div>
                )}

                {/* Email Drafts */}
                {lead.drafts && Object.keys(lead.drafts).length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 flex items-center gap-1">
                        <Icon emoji="📧" size="text-xs" /> Follow-up Drafts
                      </p>
                    </div>
                    <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
                      {Object.keys(lead.drafts).map((type) => (
                        <button
                          key={type}
                          onClick={() => setSelectedDraft(prev => ({ ...prev, [lead.id]: type }))}
                          className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                            selectedDraft[lead.id] === type
                              ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100'
                              : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-750'
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                    {(() => {
                      // Always show a draft (default to first available or selected)
                      const draftType = selectedDraft[lead.id] || Object.keys(lead.drafts)[0];
                      if (!draftType) return null;
                      
                      const isEditing = editingLead === lead.id;
                      const currentDraft = (isEditing && editedDrafts[lead.id]?.[draftType] !== undefined)
                        ? editedDrafts[lead.id][draftType]
                        : lead.drafts[draftType];
                      
                      return (
                        <div className="space-y-2">
                          {isEditing ? (
                            // In edit mode: show textarea for editing
                            <textarea
                              value={editedDrafts[lead.id]?.[draftType] || currentDraft || ''}
                              onChange={(e) => handleDraftChange(lead.id, draftType, e.target.value)}
                              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600 focus:border-transparent min-h-[120px] resize-y text-xs leading-relaxed"
                              placeholder="Enter your follow-up message..."
                            />
                          ) : (
                            // View mode: show personalized draft
                            <div className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                              <p className="text-xs text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
                                {(() => {
                                  const draft = getPersonalizedDraft(currentDraft, lead.convention, lead.name);
                                  // Apply validation when displaying
                                  const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
                                  return validateDraftStructure(draft, userName, lead.name);
                                })()}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Save/Cancel Buttons - Only show in edit mode, at the bottom */}
                {editingLead === lead.id && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-800">
                    <button
                      onClick={handleSaveEdit}
                      className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <Icon emoji="✅" size="text-sm" className="mr-1" /> Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 px-4 py-3 bg-zinc-200 dark:bg-zinc-700 hover:bg-zinc-300 dark:hover:bg-zinc-600 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                    >
                      <Icon emoji="❌" size="text-sm" className="mr-1" /> Cancel
                    </button>
                  </div>
                )}

                {/* Reminder Display */}
                {lead.reminderDate && (
                  <div className={`mb-4 p-3 rounded-lg border ${
                    formatReminderDate(lead.reminderDate)?.isOverdue
                      ? 'bg-red-900/20 border-red-600/50'
                      : formatReminderDate(lead.reminderDate)?.isToday
                      ? 'bg-yellow-900/20 border-yellow-600/50'
                      : 'bg-blue-900/20 border-blue-600/50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon emoji="⏰" size="text-lg" />
                          <span className={`text-sm font-medium ${
                            formatReminderDate(lead.reminderDate)?.isOverdue
                              ? 'text-red-300'
                              : formatReminderDate(lead.reminderDate)?.isToday
                              ? 'text-yellow-300'
                              : 'text-blue-300'
                          }`}>
                            Reminder: {formatReminderDate(lead.reminderDate)?.text}
                          </span>
                        </div>
                        {lead.reminderNote && (
                          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">{lead.reminderNote}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteReminder(lead.id)}
                        className="text-zinc-600 dark:text-zinc-400 hover:text-red-400 transition-colors text-xs"
                        title="Delete reminder"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions - 2x2 Grid */}
                {editingLead !== lead.id && (
                  <div className="grid grid-cols-2 gap-2">
                    {lead.email ? (
                      <button
                        onClick={() => handleSendEmail(lead)}
                        className="py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                      >
                        <Icon emoji="📧" size="text-sm" className="mr-1" /> Send Email
                      </button>
                    ) : (
                      <div></div>
                    )}
                    <button
                      onClick={() => handleOpenLinkedIn(lead)}
                      className="py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    >
                      <Icon emoji="🔗" size="text-sm" className="mr-1" /> LinkedIn
                    </button>
                    <button
                      onClick={() => handleSetReminder(lead)}
                      className={`py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                        lead.reminderDate
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                          : 'bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100'
                      }`}
                    >
                      <Icon emoji="⏰" size="text-sm" className="mr-1" /> {lead.reminderDate ? 'Edit Reminder' : 'Set Reminder'}
                    </button>
                    <button
                      onClick={() => handleEditLead(lead)}
                      className="py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    >
                      <Icon emoji="✏️" size="text-sm" className="mr-1" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteLead(lead.id)}
                      className="py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                    >
                      <Icon emoji="🗑️" size="text-sm" className="mr-1" /> Delete
                    </button>
                  </div>
                )}

                {/* Timestamp */}
                {lead.createdAt && (
                  <p className="text-xs text-zinc-600 mt-3 text-center">
                    Added {lead.createdAt.toDate ? new Date(lead.createdAt.toDate()).toLocaleDateString() : 'recently'}
                  </p>
                )}
              </div>
                ))
              )}
            </div>
          </>
        )}
      </main>

      {/* Reminder Modal */}
      {showReminderModal && selectedLeadForReminder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">Set Reminder</h3>
              <button
                onClick={() => {
                  setShowReminderModal(false);
                  setSelectedLeadForReminder(null);
                  setReminderDays(7);
                  setReminderNote('');
                }}
                className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-zinc-700 dark:text-zinc-300 mb-2">
                  Remind me about <span className="font-semibold">{selectedLeadForReminder.name || 'this lead'}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-2">
                  Remind me in (days)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={reminderDays}
                    onChange={(e) => setReminderDays(Math.max(1, parseInt(e.target.value) || 1))}
                    className="flex-1 px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-600"
                  />
                  <div className="flex gap-1">
                    {[1, 3, 7, 14, 30].map((days) => (
                      <button
                        key={days}
                        onClick={() => setReminderDays(days)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                          reminderDays === days
                            ? 'bg-blue-600 text-white'
                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Reminder date: {(() => {
                    const date = new Date();
                    date.setDate(date.getDate() + reminderDays);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  })()}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-2">
                  Note (optional)
                </label>
                <textarea
                  value={reminderNote}
                  onChange={(e) => setReminderNote(e.target.value)}
                  placeholder="Add a note for this reminder..."
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600 min-h-[80px] resize-y"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowReminderModal(false);
                    setSelectedLeadForReminder(null);
                    setReminderDays(7);
                    setReminderNote('');
                  }}
                  className="flex-1 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveReminder}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Save Reminder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsList;

